import React, { useState, useEffect } from "react";

import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";

import { apiClient } from "../../api/client";
import {
  CreateSchoolContactRequest,
  GetCrmPositionsResponse,
  SchoolContactWithPermissionResponse,
  UpdateSchoolContactRequest,
} from "../../api/PartnerPortalGatewayApi";
import { useNotification } from "../../contexts/NotificationContext";

// Available permissions - These match the PartnerPortalPages enum from the original Partner Portal
const AVAILABLE_PERMISSIONS = ["ContactManagement", "TermDates", "ConfirmChildren"];

// Fixed permissions that all contacts get by default (as mentioned in the original UI)
const _FIXED_PERMISSIONS = ["News", "Attendance Data", "Escalation Path"];

interface SchoolContactFormProps {
  contact?: SchoolContactWithPermissionResponse | null;
  serviceId: number;
  positions: GetCrmPositionsResponse[];
  onSubmit: () => Promise<void>;
  onCancel: () => void;
}

const validationSchema = Yup.object({
  firstName: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be no more than 50 characters")
    .matches(/^[a-zA-Z\s\-']+$/, "First name can only contain letters, spaces, hyphens, and apostrophes"),
  lastName: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be no more than 50 characters")
    .matches(/^[a-zA-Z\s\-']+$/, "Last name can only contain letters, spaces, hyphens, and apostrophes"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required")
    .max(255, "Email must be no more than 255 characters"),
  phone: Yup.string()
    .nullable()
    .notRequired()
    .test("phone-validation", "Invalid phone number format", function (value) {
      if (!value || value.length === 0) {
        return true; // Allow empty phone numbers
      }
      // Check format
      if (!/^[\d\s\-+()]+$/.test(value)) {
        return this.createError({ message: "Phone number can only contain digits, spaces, hyphens, and parentheses" });
      }
      // Check length
      if (value.length < 8) {
        return this.createError({ message: "Phone number must be at least 8 digits" });
      }
      if (value.length > 20) {
        return this.createError({ message: "Phone number must be no more than 20 characters" });
      }
      return true;
    }),
  positionId: Yup.number().required("Position is required").positive("Please select a valid position"),
  additionalPermissions: Yup.array().of(Yup.string()).notRequired(), // Additional permissions are optional since fixed permissions are always included
});

export const SchoolContactForm: React.FC<SchoolContactFormProps> = ({
  contact,
  serviceId,
  positions,
  onSubmit,
  onCancel,
}) => {
  const [emailExists, setEmailExists] = useState(false);
  const { showSuccess, showError } = useNotification();

  const isEditing = !!contact;

  // Helper function for additional permissions (beyond the fixed ones)
  const getInitialAdditionalPermissions = (contactPermissions?: string[]) => {
    if (!contactPermissions) return [];

    // Return only the additional permissions that are selectable
    return contactPermissions.filter((permission) => AVAILABLE_PERMISSIONS.includes(permission));
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (request: CreateSchoolContactRequest) =>
      apiClient.schoolContact.createSchoolContact(serviceId, request),
    onSuccess: (response, variables) => {
      if (response.data) {
        showSuccess(`Contact ${variables.firstName} ${variables.lastName} has been created successfully.`);
        onSubmit();
      } else {
        showError("Failed to create contact: No data returned from server");
      }
    },
    onError: (error) => {
      console.error("Error creating contact:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      showError(`Failed to create contact: ${errorMessage}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ contactId, request }: { contactId: number; request: UpdateSchoolContactRequest }) =>
      apiClient.schoolContact.updateSchoolContact(serviceId, contactId, request),
    onSuccess: (response, variables) => {
      if (response.data) {
        showSuccess(
          `Contact ${variables.request.firstName} ${variables.request.lastName} has been updated successfully.`,
        );
        onSubmit();
      } else {
        showError("Failed to update contact: No data returned from server");
      }
    },
    onError: (error) => {
      console.error("Error updating contact:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      showError(`Failed to update contact: ${errorMessage}`);
    },
  });

  const loading = createMutation.isPending || updateMutation.isPending;

  const formik = useFormik({
    initialValues: {
      firstName: contact?.firstName || "",
      lastName: contact?.lastName || "",
      email: contact?.email || "",
      phone: contact?.phone || "",
      positionId: contact?.positionId || "",
      isPrimary: contact?.isPrimaryContact || false,
      additionalPermissions: getInitialAdditionalPermissions(contact?.permissions),
    },
    validationSchema,
    onSubmit: async (values) => {
      // Combine fixed permissions with additional permissions
      const allPermissions = [...values.additionalPermissions];

      if (isEditing && contact) {
        const updateRequest = new UpdateSchoolContactRequest({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          positionId: values.positionId as number,
          permissions: allPermissions,
        });
        updateMutation.mutate({ contactId: contact.schoolContactId, request: updateRequest });
      } else {
        const createRequest = new CreateSchoolContactRequest({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          positionId: values.positionId as number,
          isPrimary: values.isPrimary,
          permissions: allPermissions,
        });
        createMutation.mutate(createRequest);
      }
    },
  });

  // Query for checking email availability
  const emailCheckQuery = useQuery({
    queryKey: ["email-check", formik.values.email],
    queryFn: async () => {
      if (!formik.values.email || isEditing) {
        return false;
      }
      const existingContacts = await apiClient.schoolContact.listServicesForContact();
      return existingContacts.some((c: any) => c.email === formik.values.email);
    },
    enabled: !isEditing && !!formik.values.email && formik.values.email.length > 0,
    staleTime: 500,
  });

  // Update emailExists state when query data changes
  useEffect(() => {
    setEmailExists(emailCheckQuery.data || false);
  }, [emailCheckQuery.data]);

  const handlePermissionChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    formik.setFieldValue("additionalPermissions", typeof value === "string" ? value.split(",") : value);
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            id="firstName"
            name="firstName"
            label="First Name"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            required
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            id="lastName"
            name="lastName"
            label="Last Name"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
            required
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={(formik.touched.email && Boolean(formik.errors.email)) || emailExists}
            helperText={
              (formik.touched.email && formik.errors.email) || (emailExists && "This email is already in use")
            }
            required
            disabled={isEditing}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            id="phone"
            name="phone"
            label="Phone Number"
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth required error={formik.touched.positionId && Boolean(formik.errors.positionId)}>
            <InputLabel>Position</InputLabel>
            <Select
              id="positionId"
              name="positionId"
              value={formik.values.positionId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Position"
            >
              {positions.map((position) => (
                <MenuItem key={position.positionId} value={position.positionId}>
                  {position.name}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.positionId && formik.errors.positionId && (
              <FormHelperText>{formik.errors.positionId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                id="isPrimary"
                name="isPrimary"
                checked={formik.values.isPrimary}
                onChange={formik.handleChange}
                color="primary"
              />
            }
            label="Primary Contact"
          />
        </Grid>

        <Grid size={12}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Access Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All contacts will be given access to: <strong>News, Attendance Data, Escalation Path</strong>
            </Typography>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Additional Permissions</InputLabel>
            <Select
              multiple
              value={formik.values.additionalPermissions}
              onChange={handlePermissionChange}
              input={<OutlinedInput label="Additional Permissions" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {AVAILABLE_PERMISSIONS.map((permission) => (
                <MenuItem key={permission} value={permission}>
                  {permission}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select additional permissions beyond the default access (News, Attendance Data, Escalation Path)
            </FormHelperText>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            {!isEditing && (
              <Typography variant="body2" color="text.secondary">
                A confirmation email with the contact's login and password will be sent automatically
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid size={12}>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button onClick={onCancel} color="inherit" disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || emailExists || !formik.isValid}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              {loading ? "Saving..." : isEditing ? "Update Contact" : "Create Contact"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
